import { ChevronDownIcon, ChevronRightIcon, XIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { launchElapsed, type WorktreeLaunchProgress } from "./worktreeLaunchProgress";
import { WorktreeLaunchStageRow } from "./WorktreeLaunchStageRow";
import { Button } from "../ui/button";
import { WorktreeLaunchDetails } from "./WorktreeLaunchDetails";

const HEADINGS = {
  failed: "Worktree setup failed",
  cancelled: "Launch cancelled",
  done: "Worktree ready",
  running: "Setting up worktree…",
};

// The synthetic pre-activity admission progress leads with an "admit" stage
// that has no real worktree.* activity behind it yet, so it gets its own
// heading rather than "Setting up worktree…".
function resolveHeading(progress: WorktreeLaunchProgress): string {
  if (progress.stages[0]?.id === "admit" && progress.stages[0].status === "running") {
    return "Preparing environment…";
  }
  // The setup script doesn't gate the agent's launch, so a done worktree
  // with a warned setup script is still "ready" — just worth calling out,
  // not "failed" (that's the whole worktree, a launch that never reached
  // an agent at all).
  if (progress.phase === "done" && progress.stages.some((stage) => stage.status === "warning")) {
    return "Worktree ready, setup script failed";
  }
  return HEADINGS[progress.phase];
}

function resolveHeadingTone(progress: WorktreeLaunchProgress): string {
  if (progress.phase === "failed") return "text-destructive-foreground";
  if (progress.phase === "done" && progress.stages.some((stage) => stage.status === "warning")) {
    return "text-warning-foreground";
  }
  return "text-muted-foreground";
}

function summarizeError(error: string | null): string | undefined {
  const line = error?.split(/[\r\n]+/).findLast((line) => line.trim());
  return line && line.length > 240
    ? `${line.slice(0, 240)}… Open Details for the full error.`
    : line;
}

function useLaunchClock(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [active]);
  return now;
}

export interface WorktreeSetupCardProps {
  readonly progress: WorktreeLaunchProgress;
  readonly onCancel: (() => void) | null;
  readonly onUseProjectCheckout: (() => void) | null;
  readonly onEditMessage: (() => void) | null;
  readonly actionError?: string | null;
  readonly busy?: boolean;
}

function WorktreeLaunchAlerts(props: {
  readonly actionError: string | null | undefined;
  readonly errorSummary: string | undefined;
}) {
  if (!props.errorSummary && !props.actionError) return null;
  return (
    <>
      {props.errorSummary ? (
        <p role="alert" className="mt-1 ml-8 break-words text-xs text-muted-foreground">
          {props.errorSummary}
        </p>
      ) : null}
      {props.actionError ? (
        <p role="alert" className="mt-1 ml-8 break-words text-xs text-muted-foreground">
          {props.actionError}
        </p>
      ) : null}
    </>
  );
}

function WorktreeLaunchActions(props: {
  readonly busy: boolean;
  readonly detailsOpen: boolean;
  readonly onCancel: (() => void) | null;
  readonly onEditMessage: (() => void) | null;
  readonly onToggleDetails: () => void;
  readonly onUseProjectCheckout: (() => void) | null;
}) {
  const hasRightActions = props.onUseProjectCheckout || props.onEditMessage || props.onCancel;
  return (
    // No left margin, deliberately. Upstream t3code indents this row so the
    // "Details" *text* lines up with the stage *label* text; Forge aligns it
    // with the stage *icon* column instead — the chevron shares the vertical
    // channel with the ✓/○/spinner glyphs, so the row reads as the foot of the
    // stage list rather than a further indent level. That divergence is a
    // product decision, not a port bug; don't "restore" the upstream indent.
    //
    // Zero is exact rather than approximate, and holds at any root font size:
    //   stage glyph left = li px-0.5 (0.125rem) + (size-6 − size-4)/2 (0.25rem)
    //                    = 0.375rem from the stage list's edge
    //   chevron left     = border 1px + px-[calc(--spacing(2)-1px)] + [&_svg]:-mx-0.5
    //                    = 1px + (0.5rem − 1px) − 0.125rem = 0.375rem
    // Verified by getBoundingClientRect in WorktreeSetupCard.alignment.browser.tsx
    // (delta 0px; the previous label-aligned margin measured 10px right of the
    // icon column). The chevron's left edge doesn't depend on its own size, so
    // the sm: size-4 → size-3.5 swap leaves the alignment alone.
    <div className="mt-0.5 flex flex-wrap items-center gap-0.5">
      <Button
        type="button"
        size="xs"
        variant="ghost-muted"
        aria-expanded={props.detailsOpen}
        onClick={props.onToggleDetails}
      >
        {props.detailsOpen ? <ChevronDownIcon aria-hidden /> : <ChevronRightIcon aria-hidden />}
        Details
      </Button>
      {hasRightActions ? (
        <div className="ml-auto flex flex-wrap items-center gap-0.5">
          {props.onUseProjectCheckout ? (
            <Button
              type="button"
              size="xs"
              variant="ghost-muted"
              disabled={props.busy}
              onClick={props.onUseProjectCheckout}
            >
              Use project checkout
            </Button>
          ) : null}
          {props.onEditMessage ? (
            <Button
              type="button"
              size="xs"
              variant="ghost-muted"
              disabled={props.busy}
              onClick={props.onEditMessage}
            >
              Edit message
            </Button>
          ) : null}
          {props.onCancel ? (
            <Button
              type="button"
              size="xs"
              variant="ghost-muted"
              disabled={props.busy}
              onClick={props.onCancel}
            >
              <XIcon aria-hidden />
              {props.busy ? "Cancelling…" : "Cancel"}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export function WorktreeSetupCard({
  progress,
  onCancel,
  onUseProjectCheckout,
  onEditMessage,
  actionError,
  busy = false,
}: WorktreeSetupCardProps) {
  const now = useLaunchClock(progress.phase === "running");
  const [detailsOpen, setDetailsOpen] = useState(false);
  const elapsed = launchElapsed(progress.startedAt, progress.endedAt, now);
  const errorSummary = summarizeError(progress.error);
  const heading = resolveHeading(progress);
  const headingTone = resolveHeadingTone(progress);
  return (
    <section aria-label="Worktree setup" data-worktree-setup-phase={progress.phase}>
      <div className="border-b border-border/60 pb-2 pt-1">
        <div
          className={`flex h-6 min-w-0 items-baseline gap-2 px-1 text-sm leading-relaxed tabular-nums ${headingTone}`}
        >
          <span role="status" className="min-w-0 shrink truncate">
            {heading}
          </span>
          {elapsed ? (
            <span className="ml-auto shrink-0 text-xs text-muted-foreground">{elapsed}</span>
          ) : null}
        </div>
        {progress.note ? (
          <p role="status" className="mt-1 truncate px-1 text-xs text-muted-foreground">
            {progress.note}
          </p>
        ) : null}
      </div>
      <ol className="pt-1.5">
        {progress.stages.map((stage) => (
          <WorktreeLaunchStageRow
            key={stage.id}
            stage={stage}
            now={progress.endedAt ? Date.parse(progress.endedAt) : now}
          />
        ))}
      </ol>
      <WorktreeLaunchAlerts actionError={actionError} errorSummary={errorSummary} />
      {detailsOpen ? <WorktreeLaunchDetails progress={progress} /> : null}
      <WorktreeLaunchActions
        busy={busy}
        detailsOpen={detailsOpen}
        onCancel={onCancel}
        onEditMessage={onEditMessage}
        onToggleDetails={() => setDetailsOpen((open) => !open)}
        onUseProjectCheckout={onUseProjectCheckout}
      />
    </section>
  );
}
